import React, { useEffect, useState } from 'react'
import { feedService } from '@/services/feedService'
import { ReactionType } from '@/types/feed'
import type { IPost } from '@/types/feed'

const REACTION_LABELS: Record<ReactionType, string> = {
  [ReactionType.LIKE]: '👍',
  [ReactionType.CELEBRATE]: '🎉',
  [ReactionType.SUPPORT]: '🤝',
  [ReactionType.INSIGHTFUL]: '💡',
  [ReactionType.CURIOUS]: '🤔'
}

interface ReactionBarProps {
  post: IPost
  /** Latest counts from realtime updates override the post's snapshot. */
  liveCounts?: Record<ReactionType, number>
  /** Latest viewer reaction from realtime; reset to undefined on remove. */
  liveViewerReaction?: ReactionType | null
}

const ReactionBar: React.FC<ReactionBarProps> = ({ post, liveCounts, liveViewerReaction }) => {
  const initialCounts = liveCounts ?? post.reactionCounts
  const initialViewer = liveViewerReaction !== undefined ? liveViewerReaction : (post.viewerReaction ?? null)

  const [counts, setCounts] = useState<Record<ReactionType, number>>(initialCounts)
  const [viewer, setViewer] = useState<ReactionType | null>(initialViewer)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    setCounts(initialCounts)
  }, [initialCounts])

  useEffect(() => {
    setViewer(initialViewer)
  }, [initialViewer])

  const toggle = async (type: ReactionType): Promise<void> => {
    if (pending) return
    const prevViewer = viewer
    const prevCounts = counts

    const optimistic = { ...counts }
    let nextViewer: ReactionType | null

    if (prevViewer === type) {
      optimistic[type] = Math.max(0, (optimistic[type] ?? 0) - 1)
      nextViewer = null
    } else {
      if (prevViewer) optimistic[prevViewer] = Math.max(0, (optimistic[prevViewer] ?? 0) - 1)
      optimistic[type] = (optimistic[type] ?? 0) + 1
      nextViewer = type
    }

    setCounts(optimistic)
    setViewer(nextViewer)
    setPending(true)

    try {
      if (nextViewer === null) {
        await feedService.removeReaction(post.id)
      } else {
        await feedService.upsertReaction(post.id, nextViewer)
      }
    } catch {
      // Rollback on error.
      setCounts(prevCounts)
      setViewer(prevViewer)
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {Object.values(ReactionType).map((type) => {
        const count = counts[type] ?? 0
        const active = viewer === type
        return (
          <button
            key={type}
            type="button"
            onClick={() => toggle(type)}
            disabled={pending}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-50 ${
              active
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
            aria-pressed={active}
            aria-label={`React with ${type}`}
          >
            <span>{REACTION_LABELS[type]}</span>
            {count > 0 && <span className="text-xs">{count}</span>}
          </button>
        )
      })}
    </div>
  )
}

export default ReactionBar
